import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from './api';

export type Me = maintenance.components['schemas']['Me'];
export type Enums = maintenance.components['schemas']['Enums'];

interface SessionValue {
  me: Me;
  enums: Enums;
  can: (permission: string) => boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}

/** Set while signing out: the session read again after the cache is cleared answers 401, and must not win. */
let signingOut = false;

/** Sends the person to sign-in, keeping where they were (US-101). */
export function goToSignIn(reason?: 'expired') {
  if (signingOut) return;
  const params = new URLSearchParams({ returnTo: `${window.location.pathname}${window.location.search}` });
  if (reason) params.set('reason', reason);
  window.location.assign(`/sign-in?${params.toString()}`);
}

/** Loads the signed-in person and the backend enums, or redirects to sign-in. */
export function SessionProvider({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const me = useQuery({ queryKey: ['me'], queryFn: async () => unwrap(await api.maintenance.GET('/me')), retry: false });
  const enums = useQuery({ queryKey: ['enums'], queryFn: async () => unwrap(await api.maintenance.GET('/enums')), retry: false, staleTime: Infinity, enabled: me.isSuccess });

  useEffect(() => {
    if (me.error instanceof ApiFailure && me.error.status === 401) goToSignIn();
  }, [me.error]);

  useEffect(() => {
    const theme = me.data?.preferences.theme ?? 'system';
    const dark = theme === 'dark' || (theme === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  }, [me.data?.preferences.theme]);

  if (me.error && !(me.error instanceof ApiFailure && me.error.status === 401)) throw me.error;
  if (!me.data || !enums.data) return <>{fallback}</>;
  const value: SessionValue = { me: me.data, enums: enums.data, can: p => me.data.permissions.includes(p) };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return async () => {
    signingOut = true;
    await api.auth.POST('/session/logout');
    queryClient.clear();
    window.location.assign('/sign-in?reason=signed_out');
  };
}
